import { differenceInHours, differenceInMilliseconds, format } from 'date-fns';

import { Event } from '@/types/Event';
import { safeFromUnixTime } from '@/utils/dates';

export const getRandomKey = (): string => crypto.randomUUID();

export const formatTime = (date: number): string => {
    let ts = date;
    if (ts > 100_000_000_000) {
        ts = date / 1000;
    }
    return safeFromUnixTime(ts).toISOString().split('.')[0].replace(/-|,|:/g, '') + 'Z';
};

export const calculateDuration = (startTime: number, endTime: number): string => {
    let end = safeFromUnixTime(endTime / 1000);
    let start = safeFromUnixTime(startTime / 1000);
    if (startTime < 100_000_000_000) {
        end = safeFromUnixTime(endTime);
        start = safeFromUnixTime(startTime);
    }
    const difference = differenceInMilliseconds(end, start);
    const differenceHours = differenceInHours(end, start);
    return Math.floor(differenceHours) + format(safeFromUnixTime(difference / 1000), ':mm');
};

export const buildUrl = (event: Event, type: string): string => {
    let calendarUrl = '';
    switch (type) {
        case 'google':
            calendarUrl = 'https://calendar.google.com/calendar/render';
            calendarUrl += '?action=TEMPLATE';
            calendarUrl += '&dates=' + formatTime(event.startTime);
            calendarUrl += '/' + formatTime(event.endTime);
            calendarUrl += '&location=' + encodeURIComponent(event.location);
            calendarUrl += '&text=' + encodeURIComponent(event.title);
            calendarUrl += '&details=' + encodeURIComponent(event.description);
            break;
        case 'yahoo': {
            const duration = calculateDuration(event.startTime, event.endTime);
            calendarUrl = 'https://calendar.yahoo.com/?v=60&view=d&type=20';
            calendarUrl += '&title=' + encodeURIComponent(event.title);
            calendarUrl += '&st=' + formatTime(event.startTime);
            calendarUrl += '&dur=' + duration;
            calendarUrl += '&desc=' + encodeURIComponent(event.description);
            calendarUrl += '&in_loc=' + encodeURIComponent(event.location);
            break;
        }
        case 'outlookcom':
            calendarUrl = 'https://outlook.live.com/owa/?rru=addevent';
            calendarUrl += '&startdt=' + formatTime(event.startTime);
            calendarUrl += '&enddt=' + formatTime(event.endTime);
            calendarUrl += '&subject=' + encodeURIComponent(event.title);
            calendarUrl += '&location=' + encodeURIComponent(event.location);
            calendarUrl += '&body=' + encodeURIComponent(event.description);
            calendarUrl += '&allday=false';
            calendarUrl += '&uid=' + getRandomKey();
            calendarUrl += '&path=/calendar/view/Month';
            break;
        default:
            calendarUrl = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'BEGIN:VEVENT',
                'URL:' + (typeof document !== 'undefined' ? document.URL : ''),
                'DTSTART:' + formatTime(event.startTime),
                'DTEND:' + formatTime(event.endTime),
                'SUMMARY:' + event.title,
                'DESCRIPTION:' + event.description,
                'LOCATION:' + event.location,
                'END:VEVENT',
                'END:VCALENDAR',
            ].join('\n');
    }
    return calendarUrl;
};

export const isMobile = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    const agent = window.navigator.userAgent || window.navigator.vendor;
    return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        agent
    );
};
